import { useEffect, useSyncExternalStore } from "react";
import { api, ApiError } from "./api/client";
import type {
  PriceAddon,
  PriceDeal,
  PriceOption,
  PriceProduct,
  PriceRole,
  PriceSeries,
  PriceSGDeal,
  PriceSGOffer,
  PriceSGSeries,
  PriceSheet,
} from "./api/types";
import { useInterval, useOnVisible } from "./hooks";

/*
 * 价目表: what each product costs on shop.lazior.com (the old dealer shop) and
 * on store.lazior.com, for whoever is asking — customer service used to ask
 * around, or send the October PDF (owner, 2026-10-04).
 *
 * The server works the sheet out in the background and answers from memory,
 * 304 while nothing changed. One copy here serves the page and every chat's
 * 价钱 drawer: opening either costs one small request at most.
 */

interface State {
  sheet: PriceSheet | null;
  error: string;
}

let state: State = { sheet: null, error: "" };
let tag = "";
let readAt = 0;
let inflight: Promise<void> | null = null;
const subs = new Set<() => void>();

const subscribe = (f: () => void) => {
  subs.add(f);
  return () => subs.delete(f);
};
const snapshot = () => state;

/** How often an open price list asks again. The server's own sheet moves every 10 minutes. */
const POLL_MS = 10 * 60 * 1000;

/** Reads the sheet, unless it was read in the last minute (force skips that). */
export function loadPrices(force = false): Promise<void> {
  if (inflight) return inflight;
  if (!force && state.sheet && Date.now() - readAt < 60 * 1000) return Promise.resolve();
  inflight = api
    .prices(tag)
    .then(({ body, tag: t }) => {
      tag = t;
      readAt = Date.now();
      if (body) state = { sheet: body, error: "" };
      else if (state.error) state = { ...state, error: "" };
    })
    .catch((e: unknown) => {
      if (e instanceof ApiError && e.status === 401) return;
      // The server answers 503 until its first pass is done (a minute after a
      // deploy) and 501 where the price list is not wired.
      const why =
        e instanceof ApiError && e.status === 503
          ? "价目表在准备中，一分钟后会自己出来"
          : e instanceof ApiError && e.status === 501
            ? "这个环境没有接价目表"
            : e instanceof ApiError && e.status === 0
              ? "连不上服务器，等一下会自己再试"
              : "读不到价目表";
      state = { ...state, error: why };
    })
    .finally(() => {
      inflight = null;
      subs.forEach((f) => f());
    });
  return inflight;
}

/** The sheet for a page or drawer that is showing (`active`), kept fresh while it shows. */
export function usePrices(active: boolean): State {
  const s = useSyncExternalStore(subscribe, snapshot);
  useEffect(() => {
    if (active) void loadPrices();
  }, [active]);
  useInterval(() => void loadPrices(true), POLL_MS, active, { fireOnEnable: false });
  // Nothing to show yet (the server's first pass, a dropped connection): try again soon.
  useInterval(() => void loadPrices(true), 30 * 1000, active && !s.sheet, { fireOnEnable: false });
  useOnVisible(() => void loadPrices(), active);
  return s;
}

/* ---- words ---- */

export const ROLE_NAME: Record<PriceRole, string> = {
  customer: "客户",
  vip: "VIP",
  agent: "Agent T1",
  leader: "Leader T2",
  excellence: "Excellence T3",
  elite: "Elite T4",
  distributor: "经销商",
};

/** The dealer tiers under 代理, lowest first. 经销商 has its own button. */
export const AGENT_TIERS: PriceRole[] = ["agent", "leader", "excellence", "elite"];

export const isDealer = (r: PriceRole) => r !== "customer" && r !== "vip";

/** "RM136", "RM6.90", "RM1,050". */
export function rm(sen: number): string {
  const whole = sen % 100 === 0;
  return "RM" + (sen / 100).toLocaleString("en-US", { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
}

const MYT = "Asia/Kuala_Lumpur";

/** "10/31" in Malaysia. */
export function md(iso: string): string {
  const d = new Date(iso);
  const p = new Intl.DateTimeFormat("en-US", { timeZone: MYT, month: "numeric", day: "numeric" }).formatToParts(d);
  return `${p.find((x) => x.type === "month")?.value}/${p.find((x) => x.type === "day")?.value}`;
}

/** A storefront promotion's last day: its end is exclusive (shop_campaigns.ends_at). */
export const storeLastDay = (iso: string) => md(new Date(Date.parse(iso) - 1000).toISOString());

/** Whole days from now to iso, counted in Malaysian calendar days. */
export function daysLeft(iso: string, now = Date.now()): number {
  const day = (t: number) => {
    const s = new Intl.DateTimeFormat("en-CA", { timeZone: MYT }).format(new Date(t));
    return Date.parse(s + "T00:00:00Z") / 86400000;
  };
  return Math.round(day(Date.parse(iso)) - day(now));
}

/** "12 分钟前", "3 小时前". */
export function ago(iso: string, now = Date.now()): string {
  const min = Math.max(0, Math.round((now - Date.parse(iso)) / 60000));
  if (min < 1) return "刚刚";
  if (min < 60) return `${min} 分钟前`;
  const h = Math.round(min / 60);
  return h < 48 ? `${h} 小时前` : `${Math.round(h / 24)} 天前`;
}

/* ---- reading a product ---- */

/** A product's series; an empty one when the sheet has none for it. */
export function seriesOf(sheet: PriceSheet, p: PriceProduct): PriceSeries {
  return sheet.series[p.series] ?? { name: p.name, unit: p.unit };
}

/** The other products a product may be mixed with in one set. */
export function matesOf(sheet: PriceSheet, p: PriceProduct): PriceProduct[] {
  if (!seriesOf(sheet, p).mix) return [];
  return sheet.products.filter((x) => x.series === p.series && x.key !== p.key);
}

/** The deals a role may buy, running first. */
export function dealsFor(p: PriceProduct, role: PriceRole): PriceDeal[] {
  return (p.deals ?? []).filter((d) => !d.roles?.length || d.roles.includes(role));
}

/** One deal's parts the way a colleague says them, one part a string: ["1 盒 RM136", "加购 1 盒 RM59"]. A page keeps each
 *  part on one line: 「2 盒×RM136 + 加购 2 盒×」 broke before its 「RM59」 (QA V-17). */
export function dealBits(d: PriceDeal, unit: string): string[] {
  const part = (n: number, sen: number) => (n === 1 ? `1 ${unit} ${rm(sen)}` : `${n} ${unit} × ${rm(sen)}`);
  const bits: string[] = [];
  for (const x of d.core ?? []) bits.push(part(x.n, x.sen));
  for (const x of d.add ?? []) bits.push("加购 " + part(x.n, x.sen));
  if (d.free) bits.push(`送 ${d.free} ${unit}`);
  return bits;
}

/** The same in one line: "1 盒 RM136 ＋ 加购 1 盒 RM59". */
export function dealHow(d: PriceDeal, unit: string): string {
  return dealBits(d, unit).join(" ＋ ");
}

/** An add-on as the page names it. The API calls one 「加购」 when the shop's offer does not say which product it is (it can be
 *  any of several): 「可加购 Shaker RM6.90、加购 RM6.90」 read as a name gone missing (QA V-17). */
export const addonName = (a: PriceAddon) => (a.name.trim() && a.name.trim() !== "加购" ? a.name : "其他加购品");

/** The rows a role reads: a dealer reads an add-on's as a customer does (it has no dealer price). */
export function rowsFor(p: PriceProduct, role: PriceRole) {
  return p.rows[isDealer(role) ? "customer" : role] ?? [];
}

/** What the list shows beside a product for a role: one unit on each shop, or a dealer's range. */
export function headline(sheet: PriceSheet, p: PriceProduct, role: PriceRole): { shop?: number; store?: number; range?: string } {
  if (isDealer(role) && !p.addon) {
    const lad = seriesOf(sheet, p).ladders?.[role];
    if (lad?.length && p.on_shop) {
      const units = lad.map((r) => r.unit);
      const lo = Math.min(...units);
      const hi = Math.max(...units);
      return { range: lo === hi ? rm(hi) : `${rm(lo)}–${hi / 100}` };
    }
    const d = dealsFor(p, role).find((x) => x.live);
    return d ? { range: rm(d.sen) } : {};
  }
  const one = rowsFor(p, role).find((r) => r.n === 1);
  return { shop: one?.shop[0]?.sen, store: one?.store[0]?.sen };
}

/** Whether a role has a promotion running on either shop for this product. */
export function hasDeal(p: PriceProduct, role: PriceRole): boolean {
  if (dealsFor(p, role).some((d) => d.live)) return true;
  return !isDealer(role) && (p.store?.campaigns?.length ?? 0) > 0;
}

/** Everything a search may match, lower case. */
export function haystack(sheet: PriceSheet, p: PriceProduct): string {
  const s = seriesOf(sheet, p);
  // Not the series' description: 排毒's lists all four flavours, and "kiwi" is KIWINET's word alone.
  return [p.name, p.alias ?? "", s.name, ...(p.words ?? [])].join(" ").toLowerCase();
}

/* ---- the text a colleague pastes into the chat ---- */

export type Platform = "shop" | "store";

const tagWord = (o: PriceOption) => (o.tag === "deal" ? o.label || "活动" : o.tag === "addon" ? "加购" : "");

/** The price list for one product, to paste to the person asking. */
export function copyText(sheet: PriceSheet, p: PriceProduct, role: PriceRole, platform: Platform): string {
  const s = seriesOf(sheet, p);
  const unit = p.unit;
  const mates = matesOf(sheet, p).map((x) => x.name);
  const about = mates.length ? `（${s.name}，可以跟 ${mates.join(" / ")} 混搭）` : s.name !== p.name ? `（${s.name}）` : "";
  const lines: string[] = [];
  if (p.addon) {
    const o = rowsFor(p, role)[0];
    const sen = (platform === "shop" ? o?.shop[0] : o?.store[0])?.sen ?? o?.store[0]?.sen ?? o?.shop[0]?.sen;
    return `${p.name}：${sen != null ? rm(sen) : "—"}（要跟产品一起买）`;
  }
  if (isDealer(role)) {
    lines.push(`${p.name}${about} ${ROLE_NAME[role]} 拿货价（按累积销售额）`);
    for (const r of s.ladders?.[role] ?? []) {
      const from = r.from <= 1 ? "一开始" : `累积 ${rm(r.from)} 起`;
      const cart = r.cart ? `（每单满 ${rm(r.cart)}）` : "";
      lines.push(`${from}${cart}：每${unit} ${rm(r.unit)}`);
    }
    for (const d of dealsFor(p, role).filter((x) => x.live)) {
      const profit = d.profit ? `，加购的每${unit}赚 ${rm(d.profit)}` : "";
      lines.push(`${d.label || d.name}：${d.units} ${unit} ${rm(d.sen)}（${dealHow(d, unit)}${profit}）`);
    }
    return lines.join("\n");
  }
  const who = role === "vip" ? " VIP 价" : "";
  lines.push(`${p.name}${who}${about}`);
  let ends = "";
  for (const r of rowsFor(p, role)) {
    const o = platform === "shop" ? r.shop[0] : r.store[0];
    if (!o) continue;
    const word = tagWord(o);
    lines.push(`${r.n} ${unit} ${rm(o.sen)}${word ? `（${word}）` : ""}`);
  }
  if (platform === "shop") {
    const live = dealsFor(p, role).filter((d) => d.live && d.ends);
    if (live.length) ends = live.map((d) => d.ends!).sort()[0]!;
  } else {
    const c = p.store?.campaigns?.[0];
    if (c) ends = c.ends;
  }
  if (ends) lines.push(`活动到 ${platform === "store" ? storeLastDay(ends) : md(ends)}`);
  return lines.join("\n");
}

/* ---- 新加坡: the team's own list, in Singapore dollars, delivery free ---- */

/*
 * Owner, 2026-10-07: 「价目表怎么没有新加坡价格？」 — they were only in 小爱's
 * 产品资料. Neither shop sells to Singapore, so the team keeps one list here
 * (lazior-api pricelist/singapore.go) and 小爱 quotes a +65 customer from it.
 */

/** 马来西亚 (both shops, RM) or 新加坡 (the team's list, SGD). */
export type Market = "my" | "sg";

/** A Singapore number the way the shop and 小爱 read one: 65, then eight digits from 3, 6, 8 or 9. */
export const isSingaporeNumber = (waID: string) => /^\+?65[3689]\d{7}$/.test(waID.trim());

/** "SGD 53", "SGD 6.90". */
export function sgd(cents: number): string {
  const whole = cents % 100 === 0;
  return "SGD " + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
}

/** A product's Singapore prices — its series' — when it has any. */
export function sgOf(sheet: PriceSheet, p: PriceProduct): PriceSGSeries | undefined {
  const s = sheet.sg?.series[p.series];
  return s && (s.offers.length > 0 || (s.deals?.length ?? 0) > 0) ? s : undefined;
}

/** One size in the Singapore table: its price, and its promotions running or about to. */
export interface SGRow {
  n: number;
  offer?: PriceSGOffer;
  deals: PriceSGDeal[];
}

export function sgRows(s: PriceSGSeries): SGRow[] {
  const by = new Map<number, SGRow>();
  const row = (n: number) => {
    let r = by.get(n);
    if (!r) by.set(n, (r = { n, deals: [] }));
    return r;
  };
  for (const o of s.offers) row(o.n).offer = o;
  for (const d of s.deals ?? []) if (d.state !== "ended") row(d.n).deals.push(d);
  return [...by.values()].sort((a, b) => a.n - b.n);
}

/** The Singapore promotions running today. */
export const sgLive = (s?: PriceSGSeries) => (s?.deals ?? []).filter((d) => d.state === "live");

/** One unit's Singapore price, beside a product in the list. */
export const sgHeadline = (sheet: PriceSheet, p: PriceProduct) => sgOf(sheet, p)?.offers.find((o) => o.n === 1)?.cents;

/** "10/31" from "2026-10-31". */
export function sgDay(d: string): string {
  const [, m, day] = d.split("-");
  return `${Number(m)}/${Number(day)}`;
}

/** Days from today to a promotion's last day, in Malaysia. */
export const sgDaysLeft = (d: string, now = Date.now()) => daysLeft(`${d}T12:00:00+08:00`, now);

/** The text to paste to a customer in Singapore. */
export function sgCopyText(sheet: PriceSheet, p: PriceProduct): string {
  const s = sgOf(sheet, p);
  if (!s) return "";
  const series = seriesOf(sheet, p);
  const mates = matesOf(sheet, p).map((x) => x.name);
  const about = mates.length ? `（${series.name}，可以跟 ${mates.join(" / ")} 混搭）` : series.name !== p.name ? `（${series.name}）` : "";
  const lines = [`${p.name}${about}`, "新加坡价（新币 SGD，包邮）"];
  for (const r of sgRows(s)) {
    if (r.offer) lines.push(`${r.n} ${p.unit} ${sgd(r.offer.cents)}${r.offer.was ? `（原价 ${sgd(r.offer.was)}）` : ""}`);
    for (const d of r.deals.filter((x) => x.state === "live")) {
      lines.push(`${r.n} ${p.unit} ${sgd(d.cents)}（${d.label}${d.was ? `，原价 ${sgd(d.was)}` : ""}）`);
    }
  }
  const ends = sgLive(s)
    .map((d) => d.ends)
    .filter((e): e is string => !!e)
    .sort()[0];
  if (ends) lines.push(`活动到 ${sgDay(ends)}`);
  return lines.join("\n");
}

/** Copies text; the old way when the clipboard is refused (http, an old phone). */
export async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    ta.remove();
    return ok;
  }
}
